<script lang="ts">
  import { toPersianDigits } from '@clinic/money-date';
  import { tick, untrack } from 'svelte';
  import { fa } from '../i18n/fa';
  import { cn } from '../lib/cn';
  import { useWorkInProgress } from '../pwa/update.svelte';
  import { location, navigate } from '../router/router.svelte';
  import Button from '../ui/Button.svelte';
  import {
    type Box,
    arrowStep,
    clipToViewport,
    needsNavigation,
    placeCard,
    scrollDelta,
    spotlightBox,
    stepHref,
  } from './steps';
  import { tour } from './tour.svelte';

  /**
   * Walks the member through real pages (employer request 2026-10-13): for each step it opens the
   * page with the app router, waits for the element marked data-tour="…", scrolls it into view, dims
   * the rest of the page with a cut-out and puts a card beside it (a bottom sheet on a phone). A
   * missing target (empty state, hidden section) shows the card centred; it never waits for ever.
   * A native <dialog> makes the page behind inert (focus stays on the card, clicks do nothing) and
   * Escape skips the tour. The PWA never reloads the page while it runs.
   */
  const TARGET_TIMEOUT_MS = 4000;
  const POLL_MS = 150;
  const id = $props.id();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;

  useWorkInProgress(() => tour.running);

  let dialog = $state<HTMLDialogElement>();
  let titleEl = $state<HTMLElement>();
  let viewport = $state({ width: window.innerWidth, height: window.innerHeight });
  let cardWidth = $state(360);
  let cardHeight = $state(220);
  // The part of the target inside the viewport, or null while it is missing.
  let box = $state<Box | null>(null);
  let ready = $state(false);

  const mobile = $derived(viewport.width < 768);
  const step = $derived(tour.current);
  const last = $derived(tour.index >= tour.steps.length - 1);
  const placement = $derived(placeCard({ target: box, card: { width: cardWidth, height: cardHeight }, viewport, mobile }));
  const spot = $derived(box && box.width > 0 && box.height > 0 ? spotlightBox(box) : null);
  const cardStyle = $derived(
    placement.mode === 'floating'
      ? `top:${placement.top}px;left:${placement.left}px;width:min(360px,calc(100vw - 32px))`
      : placement.mode === 'centered'
        ? 'top:50%;left:50%;transform:translate(-50%,-50%);width:min(380px,calc(100vw - 32px))'
        : '',
  );

  $effect(() => {
    if (dialog && !dialog.open) dialog.showModal();
  });

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const same = (a: Box | null, b: Box | null) =>
    a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

  // Room under the page for the sheet, so a target near the end of the page can still be lifted.
  $effect(() => {
    if (!tour.running || !mobile) return;
    document.body.style.paddingBottom = `${cardHeight + 16}px`;
    return () => {
      document.body.style.paddingBottom = '';
    };
  });

  // One run per step: open its page, find its target, keep the spotlight on it.
  $effect(() => {
    if (!tour.running) return;
    const current = tour.current;
    if (!current) return;
    const href = stepHref(tour.orgId, current.path);
    const started = Date.now();
    let lifted = false;

    untrack(() => {
      ready = false;
      if (needsNavigation(location, href)) {
        box = null;
        navigate(href);
      }
    });

    const find = (): HTMLElement | null => {
      if (!current.target) return null;
      for (const el of document.querySelectorAll<HTMLElement>(`[data-tour="${current.target}"]`)) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && !dialog?.contains(el)) return el;
      }
      return null;
    };

    const lift = async (el: HTMLElement) => {
      await tick();
      const r = el.getBoundingClientRect();
      const reserved = window.innerWidth < 768 ? cardHeight + 16 : 0;
      const dy = scrollDelta({ top: r.top, left: r.left, width: r.width, height: r.height }, window.innerHeight, reserved);
      if (dy !== 0) window.scrollBy({ top: dy, behavior: reduced() ? 'auto' : 'smooth' });
    };

    const measure = () => {
      const el = find();
      if (el) {
        const r = el.getBoundingClientRect();
        const next = clipToViewport(
          { top: r.top, left: r.left, width: r.width, height: r.height },
          { width: window.innerWidth, height: window.innerHeight },
        );
        if (!same(box, next)) box = next;
        if (!ready) ready = true;
        if (!lifted) {
          lifted = true;
          void lift(el);
        }
        return;
      }
      if (box) box = null;
      if (!ready && (!current.target || Date.now() - started > TARGET_TIMEOUT_MS)) ready = true;
    };

    let frame = 0;
    const soon = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        viewport = { width: window.innerWidth, height: window.innerHeight };
        measure();
      });
    };
    untrack(measure);
    const timer = setInterval(measure, POLL_MS);
    window.addEventListener('resize', soon);
    window.addEventListener('scroll', soon, true);
    return () => {
      clearInterval(timer);
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', soon);
      window.removeEventListener('scroll', soon, true);
    };
  });

  // The card takes the focus when a step is shown, so a screen reader reads its title.
  $effect(() => {
    void tour.index;
    if (ready && titleEl) untrack(() => titleEl?.focus({ preventScroll: true }));
  });

  function onKey(e: KeyboardEvent) {
    if (!tour.running || e.altKey || e.ctrlKey || e.metaKey) return;
    const rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    const move = arrowStep(e.key, rtl);
    if (move === null) return;
    e.preventDefault();
    if (move === 1) tour.next();
    else tour.back();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if tour.running && step}
  <dialog
    bind:this={dialog}
    aria-labelledby={titleId}
    aria-describedby={bodyId}
    data-testid="page-tour"
    onclose={() => {
      // Escape and the back button are left to the browser, which closes the dialog first.
      if (tour.running) tour.end('skipped');
    }}
    class="m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden border-0 bg-transparent p-0 text-text backdrop:bg-transparent"
  >
    {#if spot}
      <div
        aria-hidden="true"
        class="pointer-events-none fixed rounded-lg motion-safe:transition-[top,left,width,height] motion-safe:duration-200"
        style={`top:${spot.top}px;left:${spot.left}px;width:${spot.width}px;height:${spot.height}px;box-shadow:0 0 0 100vmax var(--scrim)`}
      ></div>
    {:else}
      <div aria-hidden="true" class="fixed inset-0 bg-[var(--scrim)]"></div>
    {/if}

    <div
      bind:clientWidth={cardWidth}
      bind:clientHeight={cardHeight}
      data-testid="page-tour-card"
      style={cardStyle}
      class={cn(
        'fixed overflow-y-auto border border-border-strong bg-surface p-4',
        placement.mode === 'sheet'
          ? 'inset-x-0 bottom-0 max-h-[70dvh] rounded-t-xl border-x-0 border-b-0 pb-[max(1rem,env(safe-area-inset-bottom))]'
          : 'max-h-[calc(100dvh-32px)] rounded-xl',
        !ready && 'invisible',
      )}
    >
      <div class="flex items-center justify-between gap-3">
        <p class="text-xs text-text-3" data-testid="page-tour-counter">
          {fa.tour.counter(toPersianDigits(tour.index + 1), toPersianDigits(tour.steps.length))}
        </p>
        <Button variant="ghost" size="sm" onclick={() => tour.end('skipped')}>{fa.tour.skip}</Button>
      </div>
      <h2 id={titleId} bind:this={titleEl} tabindex="-1" class="mt-1 text-lg font-bold outline-none">{step.title}</h2>
      <p id={bodyId} class="mt-2 text-sm leading-6 text-text-2">{step.body}</p>
      <div class="mt-4 flex items-center justify-between gap-2">
        <Button variant="secondary" onclick={() => tour.back()} disabled={tour.index === 0}>{fa.tour.back}</Button>
        <Button onclick={() => tour.next()}>{last ? fa.tour.finish : fa.tour.next}</Button>
      </div>
    </div>
  </dialog>
{/if}
